import { api_fetch, invalidateOrgOptionsCache } from "@/api/client.tsx"
import { OrgOption } from "@/api/org_options.tsx"

export function isTempOrgOptionId(id: string): boolean {
  return id.startsWith("temp-")
}

export async function fetchOrgOptionsForField(fieldName: string): Promise<OrgOption[]> {
  const result = await api_fetch<OrgOption[]>("/org-options", {
    params: { field_name: fieldName },
  })
  if (!result.ok) {
    throw new Error(result.error.message || "Failed to load field options")
  }
  return result.value.data
}

/**
 * Upsert dropdown options: PATCH existing rows, POST new temp rows, DELETE removed rows.
 * Refetches from the server on success (source of truth).
 */
export async function upsertOrgOptions(
  fieldName: string,
  options: OrgOption[],
  existingOptions: OrgOption[]
): Promise<OrgOption[]> {
  const desiredIds = new Set(options.map((o) => o.id))

  for (const existing of existingOptions) {
    if (desiredIds.has(existing.id)) {
      continue
    }
    const result = await api_fetch(`/org-options/${existing.id}`, { method: "DELETE" })
    if (!result.ok) {
      throw new Error(result.error.message || `Failed to delete option "${existing.option_value}"`)
    }
  }

  for (const option of options) {
    const body = {
      field_name: fieldName,
      option_value: option.option_value,
      display_order: option.display_order,
      is_active: option.is_active,
      match_patterns: option.match_patterns ?? [],
    }

    if (isTempOrgOptionId(option.id)) {
      const result = await api_fetch<OrgOption>("/org-options", {
        method: "POST",
        body: {
          ...body,
          is_active: option.is_active ?? true,
        },
      })
      if (!result.ok) {
        throw new Error(result.error.message || `Failed to create option "${option.option_value}"`)
      }
    } else {
      const result = await api_fetch<OrgOption>(`/org-options/${option.id}`, {
        method: "PATCH",
        body,
      })
      if (!result.ok) {
        throw new Error(result.error.message || `Failed to update option "${option.option_value}"`)
      }
    }
  }

  invalidateOrgOptionsCache(fieldName)
  return fetchOrgOptionsForField(fieldName)
}
